import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { PremiumFeatureGate } from "@/components/subscription/PremiumFeatureGate"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell 
} from "recharts"
import { 
  Users, 
  TrendingUp, 
  Award, 
  Calendar, 
  Download, 
  BarChart3,
  PieChart as PieChartIcon,
  Activity
} from "lucide-react"
import { format, subDays, startOfDay, endOfDay } from "date-fns"
import { de } from "date-fns/locale"
import { DashboardLayout } from "@/components/layout/DashboardLayout"

interface AnalyticsData {
  totalCustomers: number
  totalStamps: number
  totalReferrals: number
  activeCustomers: number
  averageStampsPerCustomer: number
  dailyStats: Array<{
    date: string
    stamps: number
    referrals: number
    newCustomers: number
  }>
  topCustomers: Array<{
    name: string
    email: string
    totalStamps: number
    totalReferrals: number
    lastVisit: string
  }>
  stampTypeDistribution: Array<{
    name: string
    value: number
    color: string
  }>
}

export default function Analytics() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState("7")
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    fetchAnalyticsData()
  }, [timeRange])

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true)
      
      // Get date range
      const endDate = new Date()
      const startDate = subDays(endDate, parseInt(timeRange))
      
      // Fetch customers data
      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select('*')
      
      if (customersError) throw customersError
      
      // Fetch stamps data
      const { data: stamps, error: stampsError } = await supabase
        .from('stamps')
        .select('*, customer:customers!stamps_customer_id_fkey(full_name, email)')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
      
      if (stampsError) throw stampsError
      
      // Process data
      const totalCustomers = customers?.length || 0
      const totalStamps = stamps?.length || 0
      const totalReferrals = stamps?.filter(s => s.stamp_type === 'referral').length || 0
      const activeCustomers = customers?.filter(c => c.last_visit && 
        new Date(c.last_visit) >= subDays(new Date(), 30)).length || 0
      
      // Calculate daily stats
      const dailyStats = []
      for (let i = parseInt(timeRange) - 1; i >= 0; i--) {
        const date = subDays(endDate, i)
        const dayStart = startOfDay(date)
        const dayEnd = endOfDay(date)
        
        const dayStamps = stamps?.filter(s => {
          const stampDate = new Date(s.created_at)
          return stampDate >= dayStart && stampDate <= dayEnd
        }) || []
        
        const dayCustomers = customers?.filter(c => {
          if (!c.created_at) return false
          const createdDate = new Date(c.created_at)
          return createdDate >= dayStart && createdDate <= dayEnd
        }) || []
        
        dailyStats.push({
          date: format(date, 'dd.MM', { locale: de }),
          stamps: dayStamps.length,
          referrals: dayStamps.filter(s => s.stamp_type === 'referral').length,
          newCustomers: dayCustomers.length
        })
      }
      
      // Top customers
      const topCustomers = customers
        ?.map(customer => ({
          name: customer.full_name,
          email: customer.email,
          totalStamps: customer.visit_stamps + customer.referral_stamps,
          totalReferrals: customer.total_referrals,
          lastVisit: customer.last_visit || 'Nie'
        }))
        .sort((a, b) => b.totalStamps - a.totalStamps)
        .slice(0, 10) || []
      
      // Stamp type distribution
      const visitStamps = stamps?.filter(s => s.stamp_type === 'visit').length || 0
      const referralStamps = stamps?.filter(s => s.stamp_type === 'referral').length || 0
      
      const stampTypeDistribution = [
        { name: 'Besuche', value: visitStamps, color: '#3b82f6' },
        { name: 'Empfehlungen', value: referralStamps, color: '#10b981' }
      ]
      
      setData({
        totalCustomers,
        totalStamps,
        totalReferrals,
        activeCustomers,
        averageStampsPerCustomer: totalCustomers > 0 ? Math.round(totalStamps / totalCustomers * 10) / 10 : 0,
        dailyStats,
        topCustomers,
        stampTypeDistribution
      })
      
    } catch (error) {
      console.error('Error fetching analytics:', error)
      toast({
        title: "Fehler",
        description: "Analytics-Daten konnten nicht geladen werden.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const exportData = async (format: 'csv' | 'json') => {
    if (!data) return
    
    setExporting(true)
    try {
      let content: string
      let filename: string
      let mimeType: string
      
      if (format === 'csv') {
        // Export top customers as CSV
        const headers = ['Name', 'Email', 'Stempel Gesamt', 'Empfehlungen', 'Letzter Besuch']
        const csvRows = [
          headers.join(','),
          ...data.topCustomers.map(customer => [
            `"${customer.name}"`,
            `"${customer.email}"`,
            customer.totalStamps,
            customer.totalReferrals,
            `"${customer.lastVisit}"`
          ].join(','))
        ]
        content = csvRows.join('\n')
        const now = new Date()
        const dateStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')
        filename = `analytics-${dateStr}.csv`
        mimeType = 'text/csv'
      } else {
        // Export all data as JSON
        content = JSON.stringify(data, null, 2)
        const now = new Date()
        const dateStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')
        filename = `analytics-${dateStr}.json`
        mimeType = 'application/json'
      }
      
      // Create and download file
      const blob = new Blob([content], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast({
        title: "Erfolg",
        description: `Daten erfolgreich als ${format.toUpperCase()} exportiert.`,
      })
      
    } catch (error) {
      console.error('Error exporting data:', error)
      toast({
        title: "Fehler",
        description: "Export fehlgeschlagen.",
        variant: "destructive",
      })
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Analytics & Berichte</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Lade Analytics...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Analytics & Berichte</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Keine Daten verfügbar.</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <PremiumFeatureGate feature="hasAnalytics">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Analytics & Berichte</h1>
          <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Tage</SelectItem>
              <SelectItem value="14">14 Tage</SelectItem>
              <SelectItem value="30">30 Tage</SelectItem>
              <SelectItem value="90">90 Tage</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={() => exportData('csv')}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            CSV Export
          </Button>
          
          <Button
            variant="outline"
            onClick={() => exportData('json')}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            JSON Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Kunden</p>
                <p className="text-2xl font-bold">{data.totalCustomers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <Activity className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Aktive Kunden</p>
                <p className="text-2xl font-bold">{data.activeCustomers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-500/10 p-3 rounded-lg">
                <Award className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Stempel</p>
                <p className="text-2xl font-bold">{data.totalStamps}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-orange-500/10 p-3 rounded-lg">
                <TrendingUp className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Empfehlungen</p>
                <p className="text-2xl font-bold">{data.totalReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-purple-500/10 p-3 rounded-lg">
                <BarChart3 className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ø Stempel/Kunde</p>
                <p className="text-2xl font-bold">{data.averageStampsPerCustomer}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Übersicht</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="customers">Top Kunden</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Tägliche Aktivitäten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="stamps" fill="#3b82f6" name="Stempel" />
                    <Bar dataKey="referrals" fill="#10b981" name="Empfehlungen" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Stempel-Verteilung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.stampTypeDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.stampTypeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Trend-Analyse</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={data.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="stamps" stroke="#3b82f6" strokeWidth={2} name="Stempel" />
                  <Line type="monotone" dataKey="newCustomers" stroke="#10b981" strokeWidth={2} name="Neue Kunden" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Kunden</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.topCustomers.map((customer, index) => (
                  <div
                    key={customer.email}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center">
                        {index + 1}
                      </Badge>
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-muted-foreground">{customer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-medium">{customer.totalStamps}</p>
                        <p className="text-muted-foreground">Stempel</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{customer.totalReferrals}</p>
                        <p className="text-muted-foreground">Empfehlungen</p>
                      </div>
                      <div className="text-center min-w-24">
                        <p className="text-muted-foreground">
                          {customer.lastVisit === 'Nie' ? 'Nie' : 
                            format(new Date(customer.lastVisit), 'dd.MM.yy', { locale: de })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </PremiumFeatureGate>
      </div>
    </DashboardLayout>
  )
}